import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card } from '../components/ui/Card';
import { Table } from '../components/ui/Table';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { SearchInput } from '../components/ui/SearchInput';
import { apiList, apiCreate, apiUpdate, apiDelete, apiPost } from '../api/apiClient';
import { apiOrigin } from '../utils/surface';
import { useAuth } from '../hooks/useAuth';
import { refreshAllCounters } from '../utils/refreshCounters';

// ─── Event Types by Category ───
const EVENT_CATEGORIES: Record<string, { label: string; icon: string; events: { key: string; label: string; description: string }[] }> = {
  customer: {
    label: 'Customer Events',
    icon: '🏢',
    events: [
      { key: 'customer.created', label: 'New Customer Created', description: 'When a new customer account is added' },
      { key: 'customer.updated', label: 'Customer Updated', description: 'When customer details are modified' },
      { key: 'customer.deactivated', label: 'Customer Deactivated', description: 'When a customer is deactivated' },
      { key: 'customer.dealer_assigned', label: 'Dealer Assigned', description: 'When a customer is assigned to a dealer' },
    ],
  },
  property: {
    label: 'Property Events',
    icon: '🏠',
    events: [
      { key: 'property.created', label: 'New Property Added', description: 'When a new property is created' },
      { key: 'property.updated', label: 'Property Updated', description: 'When property details change' },
      { key: 'property.unit_added', label: 'Unit Added', description: 'When a new unit is added to a property' },
      { key: 'property.unit_removed', label: 'Unit Removed', description: 'When a unit is removed' },
      { key: 'property.intercom_configured', label: 'Intercom Configured', description: 'When intercom settings are changed' },
    ],
  },
  people: {
    label: 'People Events',
    icon: '👥',
    events: [
      { key: 'tenant.created', label: 'New Tenant Added', description: 'When a new tenant is added' },
      { key: 'tenant.removed', label: 'Tenant Removed', description: 'When a tenant is removed' },
      { key: 'tenant.moved', label: 'Tenant Moved Units', description: 'When a tenant changes units' },
      { key: 'webuser.created', label: 'New Web User', description: 'When a new web user account is created' },
      { key: 'webuser.login', label: 'Web User Login', description: 'When a web user logs in' },
      { key: 'guest.created', label: 'Guest PIN Created', description: 'When a guest PIN is generated' },
      { key: 'guest.expired', label: 'Guest PIN Expired', description: 'When a guest PIN expires' },
      { key: 'guest.revoked', label: 'Guest Access Revoked', description: 'When guest access is revoked' },
      { key: 'app.activated', label: 'Mobile App Activated', description: 'When a tenant activates the mobile app' },
      { key: 'app.code_sent', label: 'Activation Code Sent', description: 'When an activation code is emailed' },
    ],
  },
  device: {
    label: 'Device Events',
    icon: '🔌',
    events: [
      { key: 'device.online', label: 'Device Online', description: 'When a device comes online' },
      { key: 'device.offline', label: 'Device Offline', description: 'When a device goes offline' },
      { key: 'device.added', label: 'Device Added', description: 'When a new device is registered' },
      { key: 'device.removed', label: 'Device Removed', description: 'When a device is removed' },
      { key: 'device.unlock', label: 'Door Unlocked', description: 'When a door is unlocked remotely' },
      { key: 'bridge.heartbeat_missed', label: 'Bridge Heartbeat Missed', description: 'When a Bridge device misses its heartbeat' },
      { key: 'bridge.relay_triggered', label: 'Bridge Relay Triggered', description: 'When a Bridge relay is activated' },
      { key: 'camera.motion', label: 'Camera Motion Detected', description: 'When a camera detects motion' },
      { key: 'acs.sync_failed', label: 'ACS Sync Failed', description: 'When PDK/Brivo sync fails' },
    ],
  },
  billing: {
    label: 'Billing Events',
    icon: '💳',
    events: [
      { key: 'billing.invoice_created', label: 'Invoice Created', description: 'When a new invoice is generated' },
      { key: 'billing.invoice_paid', label: 'Invoice Paid', description: 'When an invoice is paid' },
      { key: 'billing.invoice_overdue', label: 'Invoice Overdue', description: 'When an invoice becomes overdue' },
      { key: 'billing.payment_failed', label: 'Payment Failed', description: 'When a payment attempt fails' },
      { key: 'billing.subscription_created', label: 'Subscription Created', description: 'When a new subscription starts' },
      { key: 'billing.subscription_canceled', label: 'Subscription Canceled', description: 'When a subscription is canceled' },
      { key: 'billing.subscription_past_due', label: 'Subscription Past Due', description: 'When a subscription payment is late' },
      { key: 'billing.rate_changed', label: 'Rate Changed', description: 'When billing rates are modified' },
      { key: 'stripe.webhook_error', label: 'Stripe Webhook Error', description: 'When a Stripe webhook fails' },
    ],
  },
  system: {
    label: 'System Events',
    icon: '⚙️',
    events: [
      { key: 'system.error', label: 'System Error', description: 'When a system error occurs' },
      { key: 'system.api_error', label: 'API Error', description: 'When an external API call fails' },
      { key: 'system.maintenance', label: 'Scheduled Maintenance', description: 'Maintenance window notifications' },
      { key: 'call.missed', label: 'Missed Call', description: 'When an intercom call goes unanswered' },
      { key: 'call.completed', label: 'Call Completed', description: 'When an intercom call is completed' },
      { key: 'video.started', label: 'Video Call Started', description: 'When a video call begins' },
    ],
  },
};

type NotificationTab = 'all' | 'passes' | 'customer' | 'property' | 'people' | 'device' | 'billing' | 'system' | 'rules';

interface NotificationRule {
  id: number | string;
  event_key: string;
  category: string;
  enabled: boolean;
  notify_email: boolean;
  notify_dashboard: boolean;
  notify_push: boolean;
  email_to?: string;
  created_by?: number;
  created_at?: string;
}

export const Notifications: React.FC = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState<NotificationTab>('all');
  const [notifications, setNotifications] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [rules, setRules] = useState<NotificationRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [visitorPasses, setVisitorPasses] = useState<any[]>([]);
  const [passMessageTarget, setPassMessageTarget] = useState<any>(null);
  const [passMessage, setPassMessage] = useState('');

  // Rule builder
  const [ruleModal, setRuleModal] = useState(false);
  const [ruleForm, setRuleForm] = useState({
    category: 'customer',
    event_key: '',
    notify_email: true,
    notify_dashboard: true,
    notify_push: false,
    email_to: '',
  });
  const [editingRule, setEditingRule] = useState<NotificationRule | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch notifications (lightweight — from existing notifications table)
      // and system_events (existing table — zero extra cost)
      const [notifData, eventData] = await Promise.all([
        apiList('notifications').catch(() => []),
        apiList('system_events').catch(() => []),
      ]);
      fetch(`${apiOrigin()}/visitors/passes`, { headers: { Authorization: `Bearer ${localStorage.getItem('intrlock_token') || ''}` } })
        .then(r => r.ok ? r.json() : []).then(r => setVisitorPasses(Array.isArray(r) ? r : [])).catch(() => setVisitorPasses([]));

      // Sort newest first
      const sortedNotifs = (notifData || []).sort((a: any, b: any) =>
        new Date(b.created_at || b._created_at).getTime() - new Date(a.created_at || a._created_at).getTime()
      );
      setNotifications(sortedNotifs);

      // Events are the audit/system log — already tracked, no extra cost
      const sortedEvents = (eventData || []).sort((a: any, b: any) =>
        new Date(b.created_at || b._created_at).getTime() - new Date(a.created_at || a._created_at).getTime()
      );
      setEvents(sortedEvents);

      // Load user's notification rules from notifications table (type='rule')
      const ruleRecords = sortedNotifs.filter((n: any) => n.type === 'rule' || n.notification_type === 'rule');
      setRules(ruleRecords.map((r: any) => ({
        id: r.id,
        event_key: r.event_key || r.event_type || '',
        category: r.category || (r.event_key || '').split('.')[0] || 'system',
        enabled: r.enabled !== false,
        notify_email: r.notify_email !== false,
        notify_dashboard: r.notify_dashboard !== false,
        notify_push: !!r.notify_push,
        email_to: r.email_to || r.email || '',
        created_by: r.created_by || r.user_id,
        created_at: r.created_at || r._created_at,
      })));
    } catch (e) {
      console.error('Failed to load notifications:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Filter notifications by tab
  const filteredNotifications = useMemo(() => {
    let items = notifications.filter((n: any) => n.type !== 'rule' && n.notification_type !== 'rule');

    if (tab !== 'all' && tab !== 'rules') {
      items = items.filter((n: any) => {
        const eventKey = n.event_type || n.event_key || n.type || '';
        const category = eventKey.split('.')[0];
        return category === tab;
      });
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter((n: any) =>
        (n.title || n.message || '').toLowerCase().includes(q) ||
        (n.body || n.description || '').toLowerCase().includes(q) ||
        (n.event_type || '').toLowerCase().includes(q)
      );
    }

    return items;
  }, [notifications, tab, search]);

  // Filter events by tab
  const filteredEvents = useMemo(() => {
    let items = events;

    if (tab !== 'all' && tab !== 'rules') {
      items = items.filter((e: any) => {
        const eventKey = e.event_type || e.type || '';
        const category = eventKey.split('.')[0];
        return category === tab;
      });
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter((e: any) =>
        (e.event_type || '').toLowerCase().includes(q) ||
        (e.message || e.description || '').toLowerCase().includes(q)
      );
    }

    return items.slice(0, 100); // Limit to 100 for performance
  }, [events, tab, search]);

  // Save rule
  const handleSaveRule = async () => {
    if (!ruleForm.event_key) return;
    setSaving(true);
    try {
      const data = {
        type: 'rule',
        notification_type: 'rule',
        event_key: ruleForm.event_key,
        event_type: ruleForm.event_key,
        category: ruleForm.category,
        enabled: true,
        notify_email: ruleForm.notify_email,
        notify_dashboard: ruleForm.notify_dashboard,
        notify_push: ruleForm.notify_push,
        email_to: ruleForm.email_to || user?.email || '',
        created_by: user?.id,
        user_id: user?.id,
        title: `Rule: ${EVENT_CATEGORIES[ruleForm.category]?.events.find(e => e.key === ruleForm.event_key)?.label || ruleForm.event_key}`,
        message: `Notification rule for ${ruleForm.event_key}`,
      };

      if (editingRule) {
        await apiUpdate('notifications', (editingRule as any).uuid || editingRule.id, data);
      } else {
        await apiCreate('notifications', data);
      }
      refreshAllCounters();
      setRuleModal(false);
      setEditingRule(null);
      await load();
    } catch (e) {
      console.error('Failed to save rule:', e);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRule = async (ruleId: number | string) => {
    if (!window.confirm('Delete this notification rule?')) return;
    try {
      await apiDelete('notifications', ruleId as number | string);
      refreshAllCounters();
      await load();
    } catch (e) {
      console.error('Failed to delete rule:', e);
    }
  };

  const handleToggleRule = async (rule: NotificationRule) => {
    try {
      await apiUpdate('notifications', (rule as any).uuid || rule.id, { enabled: !rule.enabled });
      refreshAllCounters();
      setRules(prev => prev.map(r => r.id === rule.id ? { ...r, enabled: !r.enabled } : r));
    } catch (e) {
      console.error('Failed to toggle rule:', e);
    }
  };

  const openRuleBuilder = (category?: string) => {
    setEditingRule(null);
    setRuleForm({
      category: category || 'customer',
      event_key: '',
      notify_email: true,
      notify_dashboard: true,
      notify_push: false,
      email_to: user?.email || '',
    });
    setRuleModal(true);
  };

  const editRule = (rule: NotificationRule) => {
    setEditingRule(rule);
    setRuleForm({
      category: rule.category,
      event_key: rule.event_key,
      notify_email: rule.notify_email,
      notify_dashboard: rule.notify_dashboard,
      notify_push: rule.notify_push,
      email_to: rule.email_to || '',
    });
    setRuleModal(true);
  };

  const formatTimestamp = (ts: string) => {
    if (!ts) return '—';
    const d = new Date(ts);
    return isNaN(d.getTime()) ? '—' : d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const getEventLabel = (eventKey: string) => {
    for (const cat of Object.values(EVENT_CATEGORIES)) {
      const found = cat.events.find(e => e.key === eventKey);
      if (found) return found.label;
    }
    return eventKey;
  };

  const getEventBadgeColor = (eventKey: string): string => {
    if (eventKey.includes('error') || eventKey.includes('failed') || eventKey.includes('offline') || eventKey.includes('overdue')) return 'red';
    if (eventKey.includes('created') || eventKey.includes('online') || eventKey.includes('paid') || eventKey.includes('activated')) return 'green';
    if (eventKey.includes('updated') || eventKey.includes('moved') || eventKey.includes('changed')) return 'blue';
    if (eventKey.includes('removed') || eventKey.includes('canceled') || eventKey.includes('revoked') || eventKey.includes('expired')) return 'yellow';
    return 'gray';
  };

  const tabs: { key: NotificationTab; label: string; count?: number }[] = [
    { key: 'all', label: 'All', count: notifications.filter(n => n.type !== 'rule').length },
    { key: 'passes', label: 'Visitor Passes', count: visitorPasses.filter(p => p.status === 'active').length },
    { key: 'customer', label: 'Customer', count: notifications.filter(n => (n.event_type || '').startsWith('customer.')).length },
    { key: 'property', label: 'Property', count: notifications.filter(n => (n.event_type || '').startsWith('property.')).length },
    { key: 'people', label: 'People', count: notifications.filter(n => ['tenant.', 'webuser.', 'guest.', 'app.'].some(p => (n.event_type || '').startsWith(p))).length },
    { key: 'device', label: 'Device', count: notifications.filter(n => ['device.', 'bridge.', 'camera.', 'acs.'].some(p => (n.event_type || '').startsWith(p))).length },
    { key: 'billing', label: 'Billing', count: notifications.filter(n => ['billing.', 'stripe.'].some(p => (n.event_type || '').startsWith(p))).length },
    { key: 'system', label: 'System', count: notifications.filter(n => ['system.', 'call.', 'video.'].some(p => (n.event_type || '').startsWith(p))).length },
    { key: 'rules', label: 'Rules', count: rules.length },
  ];

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" /></div>;

  return (
    <div className="shadow-page-frame legacy-dashboard-migrated">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
          <p className="text-gray-500 mt-1">Event notifications, alerts, and custom notification rules</p>
        </div>
        <Button onClick={() => openRuleBuilder()}>+ Create Rule</Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 overflow-x-auto border-b border-gray-200">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${
              tab === t.key ? 'text-brand-600 border-brand-600' : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}>
            {t.label}
            {t.count != null && t.count > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">{t.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* ═══ RULES TAB ═══ */}
      {tab === 'passes' ? (
        <Card padding={false}>
          <div className="p-4 border-b border-gray-200"><h3 className="font-semibold text-gray-900">Visitor Pass Messages</h3><p className="text-sm text-gray-500 mt-1">Send secure messages to active visitors, couriers, and temporary vendors.</p></div>
          <div className="divide-y divide-gray-200">
            {visitorPasses.length === 0 ? <div className="p-8 text-center text-gray-400">No visitor passes yet</div> : visitorPasses.map((p:any)=><div key={p.id} className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3"><div><div className="font-semibold text-gray-900">{p.visitor_name}</div><div className="text-sm text-gray-500">{p.property_name} · {p.pass_type} · ends {new Date(p.valid_until).toLocaleString()}</div></div><div className="flex items-center gap-2"><Badge status={p.status}>{p.status}</Badge><Button size="sm" disabled={p.status!=='active'} onClick={()=>setPassMessageTarget(p)}>Message</Button></div></div>)}
          </div>
        </Card>
      ) : tab === 'rules' ? (
        <div>
          <Card padding={false}>
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">Notification Rules</h3>
                <p className="text-sm text-gray-500 mt-0.5">Configure which events trigger notifications and how you're notified</p>
              </div>
              <Button size="sm" onClick={() => openRuleBuilder()}>+ Add Rule</Button>
            </div>

            {rules.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-gray-400 mb-4">No notification rules configured yet</p>
                <p className="text-sm text-gray-500 mb-4">Create rules to get notified when specific events occur — like new customers, device offline alerts, or billing issues.</p>
                <Button onClick={() => openRuleBuilder()}>Create Your First Rule</Button>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {Object.entries(EVENT_CATEGORIES).map(([catKey, cat]) => {
                  const catRules = rules.filter(r => r.category === catKey);
                  if (catRules.length === 0) return null;
                  return (
                    <div key={catKey}>
                      <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                        <span className="text-xs font-semibold text-gray-500 uppercase">{cat.icon} {cat.label}</span>
                      </div>
                      {catRules.map(rule => (
                        <div key={rule.id} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50">
                          <div className="flex items-center gap-3">
                            <button onClick={() => handleToggleRule(rule)}
                              className={`w-10 h-5 rounded-full transition-colors relative ${rule.enabled ? 'bg-green-500' : 'bg-gray-300'}`}>
                              <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${rule.enabled ? 'left-5' : 'left-0.5'}`} />
                            </button>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{getEventLabel(rule.event_key)}</p>
                              <div className="flex gap-2 mt-0.5">
                                {rule.notify_dashboard && <span className="text-xs text-gray-400">Dashboard</span>}
                                {rule.notify_email && <span className="text-xs text-gray-400">Email: {rule.email_to}</span>}
                                {rule.notify_push && <span className="text-xs text-gray-400">Push</span>}
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button className="text-xs text-brand-600 hover:underline" onClick={() => editRule(rule)}>Edit</button>
                            <button className="text-xs text-red-600 hover:underline" onClick={() => handleDeleteRule(rule.id)}>Delete</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          {/* Quick-add rule cards by category */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-6">
            {Object.entries(EVENT_CATEGORIES).map(([catKey, cat]) => (
              <div key={catKey} className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 cursor-pointer hover:shadow-md transition-shadow" onClick={() => openRuleBuilder(catKey)}>
                <div className="text-center py-2">
                  <span className="text-3xl">{cat.icon}</span>
                  <p className="font-semibold text-gray-900 mt-2">{cat.label}</p>
                  <p className="text-xs text-gray-500 mt-1">{cat.events.length} event types</p>
                  <p className="text-xs text-brand-600 mt-2">+ Add Rule</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        /* ═══ NOTIFICATION & EVENT LOG TABS ═══ */
        <div className="space-y-6">
          {/* Search */}
          <Card padding={false}>
            <div className="p-4 border-b border-gray-100">
              <SearchInput value={search} onChange={setSearch} placeholder="Search notifications and events..." />
            </div>

            {/* Notifications */}
            {filteredNotifications.length > 0 && (
              <div>
                <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                  <span className="text-xs font-semibold text-gray-500 uppercase">Notifications ({filteredNotifications.length})</span>
                </div>
                <div className="divide-y divide-gray-100">
                  {filteredNotifications.slice(0, 50).map((n: any) => (
                    <div key={n.id} className={`px-4 py-3 hover:bg-gray-50 ${!n.read ? 'bg-emerald-50/30' : ''}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <Badge color={getEventBadgeColor(n.event_type || n.type || '')}>
                            {getEventLabel(n.event_type || n.type || 'notification')}
                          </Badge>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{n.title || n.message || 'Notification'}</p>
                            <p className="text-xs text-gray-500 mt-0.5">{n.body || n.description || ''}</p>
                          </div>
                        </div>
                        <span className="text-xs text-gray-400 whitespace-nowrap ml-4">{formatTimestamp(n.created_at || n._created_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Event Log */}
            {filteredEvents.length > 0 && (
              <div>
                <div className="px-4 py-2 bg-gray-50 border-y border-gray-100">
                  <span className="text-xs font-semibold text-gray-500 uppercase">Event Log ({filteredEvents.length})</span>
                </div>
                <div className="divide-y divide-gray-100">
                  {filteredEvents.map((e: any, idx: number) => (
                    <div key={e.id || idx} className="px-4 py-2 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Badge color={getEventBadgeColor(e.event_type || e.type || '')}>
                            {e.event_type || e.type || 'event'}
                          </Badge>
                          <span className="text-sm text-gray-700">{e.message || e.description || e.event_type || '—'}</span>
                        </div>
                        <span className="text-xs text-gray-400 whitespace-nowrap">{formatTimestamp(e.created_at || e._created_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {filteredNotifications.length === 0 && filteredEvents.length === 0 && (
              <div className="p-8 text-center text-gray-400">
                No notifications or events found{tab !== 'all' ? ` in this category` : ''}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* ═══ RULE BUILDER MODAL ═══ */}
      <Modal open={ruleModal} onClose={() => { setRuleModal(false); setEditingRule(null); }} title={editingRule ? 'Edit Notification Rule' : 'Create Notification Rule'} size="lg">
        <div className="space-y-4">
          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Event Category</label>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(EVENT_CATEGORIES).map(([key, cat]) => (
                <button key={key} onClick={() => setRuleForm(f => ({ ...f, category: key, event_key: '' }))}
                  className={`px-3 py-2 text-sm rounded-lg text-center transition-colors ${
                    ruleForm.category === key ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}>
                  {cat.icon} {cat.label.replace(' Events', '')}
                </button>
              ))}
            </div>
          </div>

          {/* Event Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Event Type</label>
            <select className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              value={ruleForm.event_key} onChange={e => setRuleForm(f => ({ ...f, event_key: e.target.value }))}>
              <option value="">Select an event...</option>
              {EVENT_CATEGORIES[ruleForm.category]?.events.map(ev => (
                <option key={ev.key} value={ev.key}>{ev.label} — {ev.description}</option>
              ))}
            </select>
          </div>

          {/* Notification Channels */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Notify Via</label>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={ruleForm.notify_dashboard}
                  onChange={e => setRuleForm(f => ({ ...f, notify_dashboard: e.target.checked }))}
                  className="rounded border-gray-300 text-brand-600" />
                <span className="text-sm text-gray-700">Dashboard notification</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={ruleForm.notify_email}
                  onChange={e => setRuleForm(f => ({ ...f, notify_email: e.target.checked }))}
                  className="rounded border-gray-300 text-brand-600" />
                <span className="text-sm text-gray-700">Email notification</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={ruleForm.notify_push}
                  onChange={e => setRuleForm(f => ({ ...f, notify_push: e.target.checked }))}
                  className="rounded border-gray-300 text-brand-600" />
                <span className="text-sm text-gray-700">Push notification (mobile app)</span>
              </label>
            </div>
          </div>

          {/* Email address */}
          {ruleForm.notify_email && (
            <Input label="Email Address" type="email" value={ruleForm.email_to}
              onChange={(e: any) => setRuleForm(f => ({ ...f, email_to: e.target.value }))}
              placeholder={user?.email || 'you@example.com'} />
          )}
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button variant="secondary" onClick={() => { setRuleModal(false); setEditingRule(null); }}>Cancel</Button>
          <Button onClick={handleSaveRule} disabled={saving || !ruleForm.event_key}>
            {saving ? 'Saving...' : editingRule ? 'Update Rule' : 'Create Rule'}
          </Button>
        </div>
      </Modal>
      <Modal open={!!passMessageTarget} onClose={()=>{setPassMessageTarget(null);setPassMessage('');}} title={`Message ${passMessageTarget?.visitor_name || 'visitor'}`}>
        <div className="space-y-4"><p className="text-sm text-gray-500">This message appears immediately on the recipient's secure Visitor Pass.</p><textarea className="w-full min-h-[130px] border border-gray-300 p-3 text-sm" value={passMessage} onChange={e=>setPassMessage(e.target.value)} placeholder="Enter instructions or an update…"/><div className="flex justify-end gap-2"><Button variant="secondary" onClick={()=>setPassMessageTarget(null)}>Cancel</Button><Button disabled={!passMessage.trim()||saving} onClick={async()=>{setSaving(true);try{await apiPost(`/visitors/pass/${passMessageTarget.public_id}/messages`,{message:passMessage});setPassMessageTarget(null);setPassMessage('');}finally{setSaving(false);}}}>Send message</Button></div></div>
      </Modal>
    </div>
  );
};
